import { REFERENCE_TYPE } from '../references/referenceTypeValues';

// gloss
export const MISSION_TYPES = {
  SOCIAL_POST: 'Social Post',
  UGC: 'UGC',
  SUBMIT_UGC: 'Submit UGC',
  CUSTOM_MISSION: 'Custom Mission',
  CUSTOM: 'Custom',
  SURVEY: 'Survey',
  REFERENCE: 'Reference',
  VIDEO_TESTIMONIAL: 'Video Testimonial',
  REVIEW: 'Review',
  ONE_WAY_BLAST: 'One-way Blast',
  COMMUNITY: 'Community',
  REFERENCE_OPT_IN: 'Reference Opt-In',
  REFERRAL: 'Referral',
  REFERENCE_CALL: 'Reference Call',
  REFER_A_FRIEND: 'Refer a Friend',
} as const;

export type MissionType = (typeof MISSION_TYPES)[keyof typeof MISSION_TYPES];

// gloss
const MISSION_TYPE_ALIASES: Record<string, MissionType> = {
  'social post': MISSION_TYPES.SOCIAL_POST,
  social_post: MISSION_TYPES.SOCIAL_POST,
  socialpost: MISSION_TYPES.SOCIAL_POST,

  ugc: MISSION_TYPES.UGC,
  'submit ugc': MISSION_TYPES.SUBMIT_UGC,
  submit_ugc: MISSION_TYPES.SUBMIT_UGC,
  submitugc: MISSION_TYPES.SUBMIT_UGC,

  custom: MISSION_TYPES.CUSTOM_MISSION,
  'custom mission': MISSION_TYPES.CUSTOM_MISSION,
  custom_mission: MISSION_TYPES.CUSTOM_MISSION,
  custommission: MISSION_TYPES.CUSTOM_MISSION,
  'custom task': MISSION_TYPES.CUSTOM_MISSION,

  survey: MISSION_TYPES.SURVEY,

  reference: MISSION_TYPES.REFERENCE,

  'video testimonial': MISSION_TYPES.VIDEO_TESTIMONIAL,
  video_testimonial: MISSION_TYPES.VIDEO_TESTIMONIAL,
  videotestimonial: MISSION_TYPES.VIDEO_TESTIMONIAL,

  review: MISSION_TYPES.REVIEW,

  'one-way blast': MISSION_TYPES.ONE_WAY_BLAST,
  one_way_blast: MISSION_TYPES.ONE_WAY_BLAST,
  onewayblast: MISSION_TYPES.ONE_WAY_BLAST,

  community: MISSION_TYPES.COMMUNITY,

  'reference opt-in': MISSION_TYPES.REFERENCE_OPT_IN,
  reference_opt_in: MISSION_TYPES.REFERENCE_OPT_IN,
  'reference optin': MISSION_TYPES.REFERENCE_OPT_IN,
  referenceoptin: MISSION_TYPES.REFERENCE_OPT_IN,

  referral: MISSION_TYPES.REFERRAL,

  'reference call': MISSION_TYPES.REFERENCE_CALL,
  [REFERENCE_TYPE.REFERENCE_CALL]: MISSION_TYPES.REFERENCE_CALL,
  referencecall: MISSION_TYPES.REFERENCE_CALL,

  'refer a friend': MISSION_TYPES.REFER_A_FRIEND,
  refer_a_friend: MISSION_TYPES.REFER_A_FRIEND,
  referafriend: MISSION_TYPES.REFER_A_FRIEND,
};

// gloss
export function normalizeMissionType(missionType: string | undefined | null): MissionType | string {
  if (!missionType) return '';

  const normalized = missionType.toLowerCase().trim();

  if (Object.hasOwn(MISSION_TYPE_ALIASES, normalized)) {
    return MISSION_TYPE_ALIASES[normalized] as MissionType;
  }

  const canonicalTypes = Object.values(MISSION_TYPES);
  if (canonicalTypes.includes(missionType as MissionType)) {
    return missionType as MissionType;
  }

  return missionType;
}

export function isValidMissionType(missionType: string | undefined | null): missionType is MissionType {
  if (!missionType) return false;
  return Object.values(MISSION_TYPES).includes(missionType as MissionType);
}

// gloss
export const MISSION_TYPE_IDS = {
  CUSTOM: 1,
  SUBMIT_UGC: 2,
  CUSTOM_MISSION: 3,
  SURVEY: 4,
  REFERENCE: 5,
  VIDEO_TESTIMONIAL: 6,
  REVIEW: 7,
  ONE_WAY_BLAST: 8,
  COMMUNITY: 9,
  REFERENCE_OPT_IN: 10,
  REFERRAL: 11,
  REFERENCE_CALL: 12,
} as const;

export type MissionTypeID = (typeof MISSION_TYPE_IDS)[keyof typeof MISSION_TYPE_IDS];

const MISSION_TYPE_NAME_TO_ID: Record<MissionType, MissionTypeID> = {
  [MISSION_TYPES.CUSTOM]: MISSION_TYPE_IDS.CUSTOM,
  [MISSION_TYPES.SOCIAL_POST]: MISSION_TYPE_IDS.CUSTOM,
  [MISSION_TYPES.UGC]: MISSION_TYPE_IDS.SUBMIT_UGC,
  [MISSION_TYPES.SUBMIT_UGC]: MISSION_TYPE_IDS.SUBMIT_UGC,
  [MISSION_TYPES.CUSTOM_MISSION]: MISSION_TYPE_IDS.CUSTOM_MISSION,
  [MISSION_TYPES.SURVEY]: MISSION_TYPE_IDS.SURVEY,
  [MISSION_TYPES.REFERENCE]: MISSION_TYPE_IDS.REFERENCE,
  [MISSION_TYPES.VIDEO_TESTIMONIAL]: MISSION_TYPE_IDS.VIDEO_TESTIMONIAL,
  [MISSION_TYPES.REVIEW]: MISSION_TYPE_IDS.REVIEW,
  [MISSION_TYPES.ONE_WAY_BLAST]: MISSION_TYPE_IDS.ONE_WAY_BLAST,
  [MISSION_TYPES.COMMUNITY]: MISSION_TYPE_IDS.COMMUNITY,
  [MISSION_TYPES.REFERENCE_OPT_IN]: MISSION_TYPE_IDS.REFERENCE_OPT_IN,
  [MISSION_TYPES.REFERRAL]: MISSION_TYPE_IDS.REFERRAL,
  [MISSION_TYPES.REFERENCE_CALL]: MISSION_TYPE_IDS.REFERENCE_CALL,
  [MISSION_TYPES.REFER_A_FRIEND]: MISSION_TYPE_IDS.REFERRAL,
};

// gloss
const MISSION_TYPE_ID_TO_NAME: Record<MissionTypeID, MissionType> = {
  [MISSION_TYPE_IDS.CUSTOM]: MISSION_TYPES.SOCIAL_POST,
  [MISSION_TYPE_IDS.SUBMIT_UGC]: MISSION_TYPES.SUBMIT_UGC,
  [MISSION_TYPE_IDS.CUSTOM_MISSION]: MISSION_TYPES.CUSTOM_MISSION,
  [MISSION_TYPE_IDS.SURVEY]: MISSION_TYPES.SURVEY,
  [MISSION_TYPE_IDS.REFERENCE]: MISSION_TYPES.REFERENCE,
  [MISSION_TYPE_IDS.VIDEO_TESTIMONIAL]: MISSION_TYPES.VIDEO_TESTIMONIAL,
  [MISSION_TYPE_IDS.REVIEW]: MISSION_TYPES.REVIEW,
  [MISSION_TYPE_IDS.ONE_WAY_BLAST]: MISSION_TYPES.ONE_WAY_BLAST,
  [MISSION_TYPE_IDS.COMMUNITY]: MISSION_TYPES.COMMUNITY,
  [MISSION_TYPE_IDS.REFERENCE_OPT_IN]: MISSION_TYPES.REFERENCE_OPT_IN,
  [MISSION_TYPE_IDS.REFERRAL]: MISSION_TYPES.REFERRAL,
  [MISSION_TYPE_IDS.REFERENCE_CALL]: MISSION_TYPES.REFERENCE_CALL,
};

const asMissionTypeIDString = (value: string): number | undefined => {
  const trimmed = value.trim();
  return trimmed !== '' && /^\d+$/.test(trimmed) ? Number(trimmed) : undefined;
};

const knownMissionTypeID = (id: number): MissionTypeID | undefined =>
  Object.values(MISSION_TYPE_IDS).includes(id as MissionTypeID) ? (id as MissionTypeID) : undefined;

export function getMissionTypeID(missionType: string | number | undefined | null): MissionTypeID | undefined {
  if (missionType === undefined || missionType === null) return undefined;

  if (typeof missionType === 'number') return knownMissionTypeID(missionType);

  // why: missionTypeID is a BigInt column, so it reaches the frontend as a string — matching it as
  // why: a type name instead silently reports every mission as the wrong type.
  const asID = asMissionTypeIDString(missionType);
  if (asID !== undefined) return knownMissionTypeID(asID);

  const normalized = normalizeMissionType(missionType);
  return Object.hasOwn(MISSION_TYPE_NAME_TO_ID, normalized)
    ? MISSION_TYPE_NAME_TO_ID[normalized as MissionType]
    : undefined;
}

export function getMissionTypeName(missionTypeID: number | undefined | null): MissionType | undefined {
  if (missionTypeID === undefined || missionTypeID === null) return undefined;
  return MISSION_TYPE_ID_TO_NAME[missionTypeID as MissionTypeID];
}

export function isSurveyMission(missionType: string | number | undefined | null): boolean {
  const id = typeof missionType === 'number' ? missionType : getMissionTypeID(missionType);
  return id === MISSION_TYPE_IDS.SURVEY;
}

export function isVideoTestimonialMission(missionType: string | number | undefined | null): boolean {
  const id = typeof missionType === 'number' ? missionType : getMissionTypeID(missionType);
  return id === MISSION_TYPE_IDS.VIDEO_TESTIMONIAL;
}

export function isOneWayBlastMission(missionType: string | number | undefined | null): boolean {
  const id = typeof missionType === 'number' ? missionType : getMissionTypeID(missionType);
  return id === MISSION_TYPE_IDS.ONE_WAY_BLAST;
}

export function isReferenceOptInMission(missionType: string | number | undefined | null): boolean {
  const id = typeof missionType === 'number' ? missionType : getMissionTypeID(missionType);
  return id === MISSION_TYPE_IDS.REFERENCE_OPT_IN;
}

export function isSubmitUGCMission(missionType: string | number | undefined | null): boolean {
  const id = typeof missionType === 'number' ? missionType : getMissionTypeID(missionType);
  return id === MISSION_TYPE_IDS.SUBMIT_UGC;
}

export function isCustomMissionMission(missionType: string | number | undefined | null): boolean {
  const id = typeof missionType === 'number' ? missionType : getMissionTypeID(missionType);
  return id === MISSION_TYPE_IDS.CUSTOM_MISSION;
}

export function isReviewMission(missionType: string | number | undefined | null): boolean {
  const id = typeof missionType === 'number' ? missionType : getMissionTypeID(missionType);
  return id === MISSION_TYPE_IDS.REVIEW;
}

export function isReferenceCallMission(missionType: string | number | undefined | null): boolean {
  const id = typeof missionType === 'number' ? missionType : getMissionTypeID(missionType);
  return id === MISSION_TYPE_IDS.REFERENCE_CALL;
}

// gloss
export function supportsSurveyQuestions(missionType: string | number | undefined | null): boolean {
  const id = typeof missionType === 'number' ? missionType : getMissionTypeID(missionType);
  return id === MISSION_TYPE_IDS.SURVEY || id === MISSION_TYPE_IDS.REFERENCE_OPT_IN || id === MISSION_TYPE_IDS.REFERRAL;
}
